import React, {useEffect, useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";
import {selectContestsByIds, selectPreSelectedParlayById} from "../../stores/pickX/pickX.selectors";
import useFetchMissingContests from "../../hooks/useFetchMissingContests";
import {ParlayType, PayoutModel} from "../../constants/PickXEnums";
import {useGetRollingPrizeQuery} from "../../stores/pickX/pickX.api";
import {skipToken} from "@reduxjs/toolkit/query";
import CreateYourOwnParlayRenderer from "./renderers/CreateYourOwnParlayRenderer/CreateYourOwnParlayRenderer";
import CreateFromPreSelectedParlayRenderer from "./renderers/CreateFromPreSelectedParlayRenderer/CreateFromPreSelectedParlayRenderer";
import UserParlayRenderer from "./renderers/UserParlayRenderer/UserParlayRenderer";

import "./ParlayRenderer.style.scss";
import {useLocation, useNavigate} from "react-router-dom";
import useAppModalService from "../../hooks/useAppModalService";
import GlobalConst from "../../constants/GlobalConst";
import {ModalType} from "../AppModal/ModalTypes";
import PicksSubmissionPopUp from "../../views/PopUps/PicksSubmissionPopUp/PicksSubmissionPopUp";
import {clearPlacingParlayState} from "../../stores/pickX/pickX.slice";
import useDeviceType from "../../hooks/useDeviceType";
import {
    createNewParlayFromLastPlacedParlay,
} from "../../stores/pickX/pickX.thunks";
import GeneralAlertPopUp from "../../views/PopUps/GeneralPopUp/GeneralAlertPopUp";
import SecureDepositPopUp from "../../views/PopUps/SecureDepositPopUp/SecureDepositPopUp";
import {ErrorCodes} from "../../constants/ErrorCodes";
import {FormattedMessage} from "react-intl";

export const renderers = {
    [ParlayType.CreateYourOwn]: CreateYourOwnParlayRenderer,
    [ParlayType.CreateFromPreSelected]: CreateFromPreSelectedParlayRenderer,
    [ParlayType.UserParlay]: UserParlayRenderer,
};

const ParlayRenderer = ({parlay, prizeAsset})=>{

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {openModal} = useAppModalService();
    const contestIds = useMemo(() => parlay?.contestIds ?? [], [parlay?.contestIds]);
    const contests = useSelector(state => selectContestsByIds(state, contestIds ?? []));
    const placingParlayState = useSelector(state => state.pickX.placingParlayState);
    const ParlayRenderer = renderers[parlay?.parlayType] || (()=><div>Unknown ParlayType</div>);
    const {isMobile} = useDeviceType();
    useFetchMissingContests(contestIds);

    const isPickModel = parlay?.PayoutModel === PayoutModel.Pick12 || parlay?.PayoutModel === PayoutModel.Pick16;
    const preSelectedParlay = useSelector(state =>
        parlay?.preSelectedParlayId ? selectPreSelectedParlayById(state, parlay.preSelectedParlayId) : null
    );
    const {data: rollingPrize} = useGetRollingPrizeQuery(
        isPickModel && preSelectedParlay ? {
            currency: parlay.WagerCurrency,
            wager: parlay.Wager,
            sportId: preSelectedParlay.SportIds?.[0],
            payoutModel: parlay.PayoutModel,
        } : skipToken
    );

    useEffect(() => {

        let popup = null;
        if(placingParlayState.status === 'success')
            popup = GlobalConst.POPUPS.PARLAY_PLACED;
        else if(placingParlayState.status === 'error')
            popup = GlobalConst.POPUPS.PLACE_PARLAY_FAILED;
        else
            return;


        if (popup === GlobalConst.POPUPS.PARLAY_PLACED) {
            openModal(popup, ModalType.CUSTOM, {
                modalComponent: PicksSubmissionPopUp,
                onOk: () => {
                    dispatch(createNewParlayFromLastPlacedParlay());
                },
                onClose: () => {
                    if(placingParlayState.placedParlayId === null){
                        navigate('/Lobby');
                    }else{
                        if(isMobile)
                            navigate(`/myaccount/mypicks/alltime?parlayId=${placingParlayState.placedParlayId}`);
                    }
                },
            });
            dispatch(clearPlacingParlayState());
        }

        if(popup === GlobalConst.POPUPS.PLACE_PARLAY_FAILED) {
            const errorCode = placingParlayState.error?.code;

            if (errorCode === ErrorCodes.INSUFFICIENT_FUNDS) {
                let potentialPayout = null;
                try {
                    const effectiveRollingAmount = parlay?.RollingPrizeAmount || rollingPrize?.RollingAmount || 0;
                    const effectiveParlay = isPickModel && effectiveRollingAmount
                        ? Object.assign(Object.create(Object.getPrototypeOf(parlay)), parlay, {RollingPrizeAmount: effectiveRollingAmount})
                        : parlay;
                    const payoutInfos = effectiveParlay?.getPayouts?.(contests, 0.0) ?? [];
                    const max = payoutInfos.reduce((m, p) => Math.max(m, p.payout ?? 0), 0);
                    if (max > 0) potentialPayout = max;
                } catch (_) {}
                openModal('deposit', ModalType.CUSTOM, {
                    title: 'Secure Deposit',
                    modalComponent: SecureDepositPopUp,
                }, { inParlay: true, potentialPayout, currency: parlay?.WagerCurrency, entryFee: parlay?.Wager });
                dispatch(clearPlacingParlayState());
                return;
            }

            const content = <FormattedMessage id={`serverErrors.placeParlayFailed.${errorCode}`}/>
            openModal(popup, errorCode, {
                title: <FormattedMessage id={`serverErrors.placeParlayFailed.title`}/>,
                details: content,
                modalComponent: GeneralAlertPopUp,
            });
            dispatch(clearPlacingParlayState());
        }

    }, [placingParlayState]);


    return <div className="ParlayRenderer">
                <ParlayRenderer parlay={parlay}
                                contests={contests}
                                prizeAsset={prizeAsset}
                />
            </div>
}
export default ParlayRenderer;